/**
 * Shared AI-workspace setup consent gate.
 *
 * D1 added a consent dialog (`AiWorkspaceSetupConfirmDialog`) in front of
 * `AiToolbarButton`'s "AI Workspace" click, behind the `aiWorkspace.confirmBeforeSetup`
 * setting. D1's review found TerminalPanel's own AI-terminal creation surfaces
 * (the `+` AI Context button, the connection-selector flow) still called their
 * creation function directly, bypassing the gate. Rather than duplicate the
 * dialog wiring, both call sites share this hook.
 *
 * Any surface that wants to create an AI terminal should call `requestSetup`
 * instead of invoking its creation function directly. When the setting is on,
 * this opens the dialog (render it with `consentContext` + `handleConfirm` /
 * `handleCancel` wherever this hook is used) and defers creation until the
 * user confirms. When the setting is off, creation runs immediately —
 * byte-identical to the no-consent behavior.
 */
import { useCallback, useRef, useState } from 'react';
import { useAppStore } from '../../../store/appStore';
import { coerceDatabaseToConnectionLimit } from '../../connections/utils/connectionDatabaseLimit';
import type { ActiveConnection } from '../../connections/models/connectionTypes';

export type AiWorkspaceConsentContext = {
  connection: ActiveConnection;
  database: string | undefined;
};

export type CreateAiTerminalFn = (
  connection: ActiveConnection,
  database: string | undefined
) => void | Promise<void>;

export function useAiWorkspaceSetupConsent(createAiTerminal: CreateAiTerminalFn) {
  const [consentContext, setConsentContext] = useState<AiWorkspaceConsentContext | null>(null);

  // In-flight guard (V11): terminal creation is async and the caller's own
  // "already exists?" check only sees the session AFTER create resolves, so a
  // fast double-click could kick off two creates (two terminals/exports) for the
  // same target. Track pending creates by connectionId+database and drop any
  // second trigger while the first is still resolving.
  const inFlightRef = useRef<Set<string>>(new Set());

  const runCreate = useCallback(
    async (connection: ActiveConnection, database: string | undefined) => {
      // V2 — the single terminal-creation chokepoint. Coerce the requested
      // database to the connection's "limit to selected database" target here,
      // so an off-limit pinned database node can never seed the export —
      // whether the consent dialog was shown or skipped.
      const target = coerceDatabaseToConnectionLimit(connection.connection, database);
      const key = `${connection.id}::${target ?? ''}`;
      if (inFlightRef.current.has(key)) return;
      inFlightRef.current.add(key);
      try {
        await createAiTerminal(connection, target);
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [createAiTerminal]
  );

  const requestSetup = useCallback(
    (connection: ActiveConnection, database: string | undefined) => {
      // Read lazily so the latest setting is used at request time.
      const confirmBeforeSetup =
        useAppStore.getState().aiTerminalSettings.aiWorkspace.confirmBeforeSetup;
      if (confirmBeforeSetup) {
        setConsentContext({ connection, database });
        return;
      }
      void runCreate(connection, database);
    },
    [runCreate]
  );

  const handleConfirm = useCallback(
    (database: string | undefined) => {
      const ctx = consentContext;
      setConsentContext(null);
      if (!ctx) return;
      void runCreate(ctx.connection, database);
    },
    [consentContext, runCreate]
  );

  const handleCancel = useCallback(() => {
    setConsentContext(null);
  }, []);

  return { consentContext, requestSetup, handleConfirm, handleCancel };
}
